import type { ComponentType, ReactElement, ReactNode } from 'react'
import React, { Component, createRef, lazy, Suspense, useMemo } from 'react'
import dropRightWhile from 'lodash/dropRightWhile'
import omit from 'lodash/omit'
import once from 'lodash/once'
import uniqueId from 'lodash/uniqueId'
import without from 'lodash/without'
import { v4 } from 'uuid'

import { AbortError } from '@app/errors/AbortError'

import { AsyncQueue } from '@app/utils/AsyncQueue'
import { createSortHandler } from '@app/utils/createSortHandler'
import { captureMessage } from '@app/utils/errorReport/errorReport'
import { isKeyboardKeyEvent } from '@app/utils/isKeyboardKeyEvent'
import { noop } from '@app/utils/noop'
import { useAppDispatch } from '@app/utils/redux'
import { sink } from '@app/utils/sink'
import { sleep } from '@app/utils/sleep'

import { spawn } from '@app/packages/disposable/spawn'
import type { StackContextValue } from '@app/packages/StackContext/StackContext'
import { StackContextProvider, useStackContext } from '@app/packages/StackContext/StackContext'
import { withBoundProps } from '@app/packages/withBoundProps/withBoundProps'

import { useErrorThrow } from '@app/hooks/useErrorThrow'

import { getApplicationBridge } from '@app/store/actions/contexts/applicationBridge'
import { getKeyboardNavigation } from '@app/store/actions/contexts/keyboardNavigation'
import { withProgress, withProgressAction } from '@app/store/actions/initial'

import { ControlClasses } from '@app/components/ControlClasses/ControlClasses'
import { type ErrorBoundary } from '@app/components/ErrorBoundary'
import { type LoadingModal } from '@app/components/LoadingModal/LoadingModal'
import { RenderOnce } from '@app/components/RenderOnce/RenderOnce'

import { setMount, unsetMount } from './actions'
import { createModalTransition, type ModalTransition, ModalTransitionContext } from './ModalTransitionContext'
import { MountContext } from './MountContext'
import type { AsyncPushFunction, MountContextInterface, MountedComponentProps, PushFunction } from './types'

import classes from './Mount.module.scss'

export type { MountedComponentProps } from './types'

export const mountDummy: MountedComponentProps['mount'] = {
  id: 'dummy',
  close: () => Promise.resolve(),
  push: () => () => Promise.resolve(),
  top: true,
  registerMount: noop,
  registerOnClose: () => {},
}

export type Props = {
  className?: string
  contentClassName?: string
  children?: ReactNode
} & ReturnType<typeof useContext>

type LazyData = {
  LoadingModal: typeof LoadingModal
  ErrorBoundary: typeof ErrorBoundary
}

export interface State {
  data: null | LazyData
  stack: StackItem[]
  mounted: { [key: string]: boolean }
}

class MountContainer extends Component<Props, State> {
  state: State = {
    data: null,
    stack: [],
    mounted: {},
  }

  private stack = new DisposableStack()
  private mobileRef = createRef<HTMLDivElement>()
  private mountContext: MountContextInterface
  private queue = new AsyncQueue()
  private topLock: null | number = null

  constructor(props: Props) {
    super(props)

    this.mountContext = {
      push: this.pushAsync.bind(this),
      pop: this.pop.bind(this),
      autoclose: this.autoclose.bind(this),
      isMounted: this.checkIsMounted.bind(this),
    }
  }

  componentDidMount() {
    this.stack = new DisposableStack()
    this.props.dispatch(setMount(this.mountContext))
    this.stack.defer(() => {
      this.props.dispatch(unsetMount())
    })

    const onFirstTab = (e: KeyboardEvent) => {
      if (isKeyboardKeyEvent('Tab', e)) {
        this.props.dispatch(getKeyboardNavigation())?.increment()
      }
    }
    window.addEventListener('keydown', onFirstTab)
    this.stack.defer(() => {
      window.removeEventListener('keydown', onFirstTab)
    })

    const tm = setTimeout(() => {
      sink(
        loadLazyData().then(data => {
          this.setState({ data })
        })
      )
    }, 10000)
    this.stack.defer(() => {
      clearTimeout(tm)
    })

    const bridge = this.props.dispatch(getApplicationBridge())
    if (bridge) {
      const unsubFromApplicationBridge = bridge.addMessageListener(message => {
        if (message.type === 'pop_modal') {
          void this.pop()
        }
      })
      this.stack.defer(() => {
        unsubFromApplicationBridge()
      })
    }
  }

  componentWillUnmount() {
    this.stack.dispose()
    this.stack = new DisposableStack()
  }

  render() {
    const isMounted = this.state.stack.length > 0

    return (
      <MountContext value={this.mountContext}>
        <section className={cn(classes.mount, ControlClasses.max_height, this.props.className)}>
          <div className={ControlClasses.only_mobile} ref={this.mobileRef} />
          <div
            aria-hidden={isMounted || undefined}
            className={cn(classes.content, ControlClasses.max_height, this.props.contentClassName, { [classes.is_mounted]: isMounted })}
            style={this.topLock !== null ? { top: `-${this.topLock}px` } : undefined}
          >
            {this.props.children}
          </div>
          <RenderOnce cond={!!this.state.stack.length}>
            <div className={classes.stack}>{this.state.stack.map(this.renderStackItem)}</div>
          </RenderOnce>
        </section>
      </MountContext>
    )
  }

  private renderStackItem = (item: StackItem, _itemIndex: number) => {
    const StackItem = item.component
    let element = <StackItem {...item.props} />
    if (item.wrapper) {
      element = item.wrapper(element)
    }

    if (!this.state.data) return null
    const { LoadingModal, ErrorBoundary } = this.state.data

    return (
      <StackContextProvider key={item.key} value={item.context}>
        <ModalTransitionContext value={item.modalTransition}>
          <Suspense fallback={<LoadingModal mount={item.props.mount} name="loading-modal" />}>
            <ErrorBoundary ctx={{ mount: item.props.mount }} fallback={StackErrorFallback}>
              {element}
            </ErrorBoundary>
          </Suspense>
        </ModalTransitionContext>
      </StackContextProvider>
    )
  }

  private push: PushFunction = (component: any, options = {} as any) => {
    let afterClose: (() => unknown) | null = null

    const mountAbortController = new AbortController()

    const mountChannel = Promise.withResolvers<void>()
    mountAbortController.signal.addEventListener('abort', () => {
      mountChannel.reject(new AbortError())
    })

    mountChannel.promise = mountChannel.promise.catch(e => {
      if (e instanceof MountRegistrationFailedError) {
        captureMessage(
          `registerMount was not called in 5 seconds, check that every mount has registerMount call: ${
            stackItem.component.displayName ?? stackItem.component.name ?? 'unknown component'
          }`
        )
        return
      }

      throw e
    })

    const sleepAbortController = new AbortController()
    mountAbortController.signal.addEventListener('abort', () => {
      sleepAbortController.abort()
    })

    const id = v4()
    const onClose = async () => {
      await sleep(200)
      afterClose?.()
      options.onClose?.()
    }
    const stackItem: StackItem = {
      autoclose: true,
      ...options,
      onClose,
      scrollTop: window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0,
      component,
      key: uniqueId('mount-component-'),
      mountAbort: mountAbortController,
      modalTransition: createModalTransition(),
    }

    stackItem.context = {
      stackId: stackItem.key,
      store: {},
      abortController: mountAbortController,
      handleAction: promise => this.props.dispatch(withProgress(promise)),
      suspend: promise => {
        throw stackItem.context.handleAction(promise)
      },
    }

    let closeTr = async () => {}
    const close = async () => {
      await closeTr()
      return this.popStackItem(stackItem)
    }
    const mount: MountedComponentProps['mount'] = {
      id,
      close,
      push: this.pushAsync,
      top: false,
      registerMount: pCloseTr => {
        closeTr = pCloseTr
        sleepAbortController.abort()
        mountChannel.resolve()
      },
      registerOnClose: fn => {
        afterClose = fn
      },
    }

    stackItem.props = { ...stackItem.props, mount }

    spawn(async () => {
      await sleep(5000, sleepAbortController.signal)
      mountChannel.reject(new MountRegistrationFailedError('Mount component registration failed in a reasonable time'))
    })
    spawn(async () => {
      await this.queue.queued(async () => {
        let stack = [...this.state.stack, stackItem].sort(createSortHandler(c => [c.zIndex || 0]))

        stack = stack.map((item, index) => ({ ...item, props: { ...item.props, mount: { ...item.props.mount, top: index === stack.length - 1 } } }))

        if (this.topLock === null) {
          this.topLock = window.scrollY
        }

        await new Promise<void>(resolve => {
          this.setState({ stack }, resolve)
        })

        this.props.dispatch(getApplicationBridge())?.sendMessage({ type: 'mount_stack_change', stack_length: stack.length })

        await mountChannel.promise
        if (mountAbortController.signal.aborted) return

        this.didMount(stack)
      })()
    })

    return close
  }

  private pushAsync: AsyncPushFunction = (componentPromise: any, options = {} as any) => {
    const close = (async () => {
      if (!this.state.data) {
        try {
          const data = await this.props.dispatch(withProgress(loadLazyData()))
          this.setState({ data })
        } catch (error) {
          this.props.throwError(error as Error)
          throw error
        }
      }
      const component: any = await this.props.dispatch(withProgress(componentPromise))
      if ('preload' in component) {
        await this.props.dispatch(withProgressAction(component.preload()))
      }
      return this.push(component, options)
    })()
    return () => close.then(close => close())
  }

  private autoclose = this.queue.queued(async () => {
    if (this.state.stack.length === 0) return

    const nextStack = dropRightWhile(this.state.stack, s => !!s.autoclose)
    if (nextStack.length === this.state.stack.length) return

    const resolvedItems: StackItem[] = without(this.state.stack, nextStack as any)
    const nextMounted = omit(
      this.state.mounted,
      resolvedItems.map(item => item.key)
    )
    const leftResolvedItem = resolvedItems.at(0)

    await new Promise<void>(resolve => {
      this.setState({ stack: nextStack, mounted: nextMounted }, () => {
        resolve()
        this.props.dispatch(getApplicationBridge())?.sendMessage({ type: 'mount_stack_change', stack_length: nextStack.length })
      })
    })

    await sleep(1)
    if (leftResolvedItem) {
      window.scrollTo(0, leftResolvedItem.scrollTop)
    }

    resolvedItems.forEach(item => item.onClose())
  })

  private popStackItem = this.queue.queued(async (itemGetter: StackItem | (() => StackItem | null)) => {
    if (this.state.stack.length === 0) return

    const item = typeof itemGetter === 'function' ? itemGetter() : itemGetter
    if (!item) return
    const stackItem = this.state.stack.find(i => i.key === item.key)
    if (!stackItem) return
    stackItem.mountAbort.abort()

    const mounted = omit(this.state.mounted, stackItem.key)

    let stack = this.state.stack.filter(i => i !== stackItem)

    stack = stack.map((item, index) => ({ ...item, props: { ...item.props, mount: { ...item.props.mount, top: index === stack.length - 1 } } }))

    if (stack.length === 0) {
      this.topLock = null
    }

    await new Promise<void>(resolve => {
      this.setState({ stack, mounted }, () => {
        resolve()
        this.props.dispatch(getApplicationBridge())?.sendMessage({ type: 'mount_stack_change', stack_length: stack.length })
      })
    })

    await sleep(1)
    window.scrollTo(window.scrollX, stackItem.scrollTop)

    void stackItem.onClose()
  })

  private pop = async (): Promise<void> => {
    return this.popStackItem(() => this.state.stack.at(-1)!)
  }

  private checkIsMounted = () => {
    return this.state.stack.length > 0
  }

  private didMount = stack => {
    const lastStackItem = stack.at(-1)
    if (!lastStackItem) return
    const mounted = { ...this.state.mounted, [lastStackItem.key]: true }

    this.setState({ mounted }, () => {
      const ref = this.mobileRef.current
      if (!ref) return
      const style = window.getComputedStyle(ref, null)
      // scrolls modal to top when it opens
      if (style['display'] !== 'none') {
        window.setTimeout(() => {
          window.scrollTo(0, 0)
        }, 1)
      }
    })
  }
}

const useContext = () => {
  return {
    throwError: useErrorThrow(),
    dispatch: useAppDispatch(),
  }
}

export default withBoundProps(useContext)(MountContainer)

const loadLazyData = once(async (): Promise<LazyData> => {
  const [{ LoadingModal }, { ErrorBoundary }] = await Promise.all([
    import('@app/components/LoadingModal/LoadingModal'),
    import('@app/components/ErrorBoundary'),
  ])
  return { LoadingModal, ErrorBoundary }
})

const StackErrorFallback = lazy(() => import('./StackErrorFallback').then(m => ({ default: m.StackErrorFallback })))

interface StackItem {
  autoclose: boolean
  scrollTop: number
  key: string
  onClose: () => Promise<unknown>
  zIndex?: number

  component: ComponentType
  wrapper?: (el: ReactElement) => ReactElement
  props?: any
  mountAbort: AbortController
  context: StackContextValue
  modalTransition: ModalTransition
}

export const useMountStackContext = () => {
  const ctx = useStackContext()
  return useMemo(
    () => ({
      stackId: ctx.stackId,
      resetErrors: () => {},
    }),
    [ctx]
  )
}

class MountRegistrationFailedError extends Error {}
